import { RailChevron } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import { RailChatPreviewTooltip } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailHoverPreviewTooltip";
import { useRailRowMultiMenuGate } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import { RailRowOpenButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowOpenButton";
import {
  useRailRowSelection,
  useRegisterRailResource,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { useSingleOrDoubleClick } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useSingleOrDoubleClick";
import {
  ObjectActionMenu,
  useObjectActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import { cn } from "@/utils";
import type { InboxItem } from "@cohesive/api-client";
import { MessagesSquare } from "lucide-react";
import { useMemo, useState } from "react";

const CHAT_KIND: ObjectKind = { type: "chat" };

function chatLabel(item: InboxItem) {
  return item.flow_session?.name?.trim() || "Untitled chat";
}

export function ChatsRailChatRow({
  item,
  active,
  section = "canonical",
  showActionsMenu = false,
  onOpen,
  onToggleStar,
  onRename,
  onDelete,
  dragRef,
  dragProps,
  isDragging,
  expanded,
  onToggleExpanded,
  listItem = true,
}: {
  item: InboxItem;
  active?: boolean;
  /** Rail section this row lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  /** When true, shows the hover ⋯ menu (Chats section rows). */
  showActionsMenu?: boolean;
  onOpen: (item: InboxItem) => void;
  onToggleStar?: (item: InboxItem) => void;
  onRename: (item: InboxItem) => void;
  onDelete: (item: InboxItem) => void;
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  /**
   * When set, single-clicking the title selects AND toggles the chat's
   * nested files (a leading always-visible chevron is an instant toggle
   * too); double click opens. Omitted for flat lists, where there is
   * nothing to expand and title click only selects.
   */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  /**
   * When false, a parent drop-zone owns contiguous-selection list-item attrs.
   * Default true for flat lists (e.g. favorite chats).
   */
  listItem?: boolean;
}) {
  const menu = useRailRowContextMenu();
  const label = chatLabel(item);
  const isStarred = item.is_starred;
  // The preview must drop the moment the user opens the row: Radix's own
  // close-on-pointerdown never fires for the Open pill (it stops pointerdown
  // so the row's drag doesn't arm), and `active` — which suppresses the
  // preview of the open page — only flips once navigation lands. Re-entering
  // the row re-arms the preview (a no-longer-active row can be hovered again).
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const { selected, onActivate, rowKey, selectionAttrs } = useRailRowSelection(
    item.flow_session_id ?? item.id,
    section,
    !!active,
    "chat",
    listItem,
  );

  const registered = useMemo(
    () => ({ kind: "chat" as const, data: item }),
    [item],
  );
  useRegisterRailResource("chat", item.flow_session_id ?? item.id, registered);

  const onContextMenu = useRailRowMultiMenuGate(
    rowKey,
    showActionsMenu ? menu.onContextMenu : undefined,
  );

  // Title click selects and, when expandable, also toggles the nested files —
  // one gesture set with the workspace/folder rows. Modifier clicks are
  // multi-select gestures and must not toggle. Double click opens; the delay
  // (expandable rows only) keeps it from flipping the row open and shut on
  // the way.
  const titleClicks = useSingleOrDoubleClick(
    (e) => {
      onActivate(e);
      if (!e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
        onToggleExpanded?.();
      }
    },
    (e) => {
      onActivate(e);
      setPreviewDismissed(true);
      onOpen(item);
    },
    { delayed: !!onToggleExpanded },
  );

  const handlers = useMemo<ObjectActionHandlers>(
    () => ({
      open: () => {
        setPreviewDismissed(true);
        onOpen(item);
      },
      star: onToggleStar
        ? () => window.setTimeout(() => onToggleStar(item), 0)
        : undefined,
      unstar: onToggleStar
        ? () => window.setTimeout(() => onToggleStar(item), 0)
        : undefined,
      rename: () => window.setTimeout(() => onRename(item), 0),
      delete: () => window.setTimeout(() => onDelete(item), 0),
    }),
    [item, onOpen, onToggleStar, onRename, onDelete],
  );

  const { actions } = useObjectActions({
    kind: CHAT_KIND,
    surface: "left-rail",
    enabled: menu.open && showActionsMenu,
    state: { isFavorite: !!isStarred },
    handlers,
  });

  return (
    // The whole row is the preview trigger: the preview's position then keys
    // off the row's full width (→ the rail's right edge), never overlapping
    // the trailing Open pill / kebab the title button stops short of.
    <RailChatPreviewTooltip
      item={item}
      label={label}
      active={active}
      suppress={menu.open || previewDismissed}
    >
      <div
        ref={dragRef}
        data-rail-chat-row
        {...selectionAttrs}
        onContextMenu={onContextMenu}
        onPointerEnter={() => setPreviewDismissed(false)}
        className={cn(
          "group/row relative flex min-h-7 w-full items-center gap-2.5 px-2 hover:bg-n-05",
          showActionsMenu && "pr-8",
          !selected && "rounded-md",
          selected && "bg-foreground/[0.04] text-n-90",
          dragProps && "cursor-grab active:cursor-grabbing",
          isDragging && "opacity-50",
        )}
        {...dragProps}
      >
        {onToggleExpanded && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse chat" : "Expand chat"}
            title={expanded ? "Collapse" : "Expand"}
            className="-m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10"
          >
            <RailChevron open={expanded} />
          </button>
        )}
        <button
          type="button"
          // Single click selects + toggles (cmd/shift for multi-select only);
          // double click opens. The chevron stays as an instant toggle.
          onClick={titleClicks.onClick}
          onDoubleClick={titleClicks.onDoubleClick}
          aria-expanded={onToggleExpanded ? expanded : undefined}
          /**
           * Don't take focus on mousedown: the default blurs the composer, so
           * its focus ring would drop here and spring back once a double-click
           * open refocused it. Click still fires on mouseup; keyboard is
           * untouched. Same reasoning as the workspace rows.
           */
          onMouseDown={(e) => e.preventDefault()}
          className={cn(
            // self-stretch: keep the full row height clickable.
            "flex min-w-0 flex-1 select-none items-center gap-2.5 self-stretch text-left outline-none focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
            // Tighten the chevron→icon gap to match RailDisclosureIcon's
            // gap-1.5 (the row's gap-2.5 minus 4px).
            onToggleExpanded && "-ml-1",
          )}
        >
          <MessagesSquare className="size-4 shrink-0 text-n-50" />
          <span className="min-w-0 flex-1 truncate text-base text-n-80">
            {label}
          </span>
        </button>

        {/* Mirrors the double-click gesture: select, then open. */}
        <RailRowOpenButton
          label={label}
          onOpen={() => {
            onActivate();
            setPreviewDismissed(true);
            onOpen(item);
          }}
        />

        {showActionsMenu && (
          <ObjectActionMenu
            actions={actions}
            kind={CHAT_KIND}
            name={label}
            menuController={menu}
            label={label}
          />
        )}
      </div>
    </RailChatPreviewTooltip>
  );
}
